import axios from 'axios';

const BASE_URL = process.env.REACT_APP_STRAPI_URL || 'https://cs9.widya.io/strapi';

const strapi = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Resolves the raw URL from a media object (no base URL prepended).
 */
const resolveMediaUrl = (media) => {
  if (!media) return null;
  if (typeof media === 'string') return media;
  if (media.url) return media.url;
  if (media.data?.url) return media.data.url;
  if (media.data?.attributes?.url) return media.data.attributes.url;
  return null;
};

/**
 * Picks the best-quality image URL from a Strapi media object.
 * Priority: original (if large enough) → large format → medium → small → original fallback
 * This prevents blurry images when a tiny file was uploaded but Strapi
 * generated larger format variants.
 */
export const getStrapiMedia = (media) => {
  if (!media) return null;

  // Resolve the raw media object (handle relation wrapping)
  const obj = media?.data?.attributes || media?.data || media;

  let url = obj?.url;

  if (!url && obj?.formats) {
    url = obj.formats.large?.url || obj.formats.medium?.url || obj.formats.small?.url;
  }

  if (!url) {
    url = resolveMediaUrl(media);
  }

  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('//')) return url;

  // Special case: In this specific deployment, Strapi serves uploads under the /strapi subpath
  // BASE_URL is https://cs9.widya.io/strapi and url is /uploads/...
  return `${BASE_URL}${url}`;
};

/**
 * Maps Strapi response data for both v4 and v5.
 * Strapi v5 returns flat data, v4 nests under .attributes.
 */
export const mapStrapiData = (response) => {
  const data = response?.data?.data;
  if (!data) return null;
  if (Array.isArray(data)) {
    return data.map((item) => item.attributes || item);
  }
  return data.attributes || data;
};

/**
 * Safely renders text from Strapi rich text (Blocks JSON) or plain strings.
 * Prevents "Objects are not valid as a React child" errors.
 */
export const renderSafeText = (value, fallback = '') => {
  if (!value) return fallback;
  if (typeof value === 'string') return value;

  // Strapi Blocks: Array of objects with 'type' and 'children'
  if (Array.isArray(value)) {
    return value
      .map((block) => {
        if (block.children && Array.isArray(block.children)) {
          return block.children.map((child) => child.text || '').join('');
        }
        return '';
      })
      .join('\n\n');
  }

  // Single block object
  if (typeof value === 'object' && value.children) {
    return value.children.map((child) => child.text || '').join('');
  }

  return String(value) || fallback;
};

export { BASE_URL };
export default strapi;